import { buildCtsFileContent } from '@resistapp/common/cts-file-utils';
import { Chip, CtsColumns } from '@resistapp/common/types';
import { Dictionary, entries, flatMap, get, groupBy, keyBy, maxBy, values } from 'lodash';

type CtsWithRunDate = CtsColumns & { runDate: Date | null };
type CompileCtsFilesOptions = {
  includeQaRuleInfo?: boolean;
};

function dedupeCtRows(cts: CtsWithRunDate[]): CtsWithRunDate[] {
  const key = (r: CtsWithRunDate) => `${r.chipId}|${r.sample}|${r.assay}`;
  return values(keyBy(cts, key));
}

function selectLatestNonAy1PerAssay(cts: CtsWithRunDate[]): CtsWithRunDate[] {
  // Select latest rows per (sampleId, assay) for non-AY1 assays only
  // Non-AY1 assays maybe be re-analysed because of problems with the earlier analysis,
  // And we don't want to confuse users with the need to pick the right chip for the assay.
  // AY1 is always (and only) re-analysed together with any other assay reanalysis
  const nonAy1 = cts.filter(r => r.assay !== 'AY1');
  const bySampleAssay = groupBy(nonAy1, r => `${r.sampleId}|${r.assay}`);
  return flatMap(entries(bySampleAssay), ([, rows]) => {
    if (rows.length === 0) return [] as CtsWithRunDate[];
    const latestRunDateMs = maxBy(rows, r => r.runDate?.getTime() ?? 0)?.runDate?.getTime() ?? 0;
    const withLatestDate = rows.filter(r => (r.runDate?.getTime() ?? 0) === latestRunDateMs);
    const selectedChipId = maxBy(withLatestDate, r => r.chipId)?.chipId;
    if (selectedChipId === undefined) return [] as CtsWithRunDate[];
    return withLatestDate.filter(r => r.chipId === selectedChipId);
  });
}

/**
 * Compiles the downloadable Cq files of a project, one per chip, from the Cq rows of the project's samples
 * (as served by GET /api/project/{id}/cts).
 */
export function compileCtsFiles(
  chips: Array<Pick<Chip, 'id' | 'name' | 'runDate'>>,
  ctsRows: CtsColumns[],
  options: CompileCtsFilesOptions = {},
) {
  const { includeQaRuleInfo = false } = options;
  const runDateByChipId = keyBy(chips, c => c.id);
  const allCtsRaw: CtsWithRunDate[] = ctsRows.map(ct => ({
    ...ct,
    runDate: get(runDateByChipId, ct.chipId, undefined)?.runDate ?? null,
  }));
  const qaById = includeQaRuleInfo ? buildQaColumnById(ctsRows) : undefined;

  // 1) De-duplicate rows (the same CT row may appear under multiple assay abundance entries)
  const allCts = dedupeCtRows(allCtsRaw);

  // 2) Keep AY1 rows from all chips as-is (they are all valid)
  const ay1Rows = allCts.filter(r => r.assay === 'AY1');

  // 3) For non-AY1 assays, only keep the latest per (sample, assay)
  const latestNonAy1 = selectLatestNonAy1PerAssay(allCts);

  // 4) Merge and group by chip
  const finalRows = [...ay1Rows, ...latestNonAy1];
  const ctsByChip = groupBy(finalRows, ct => ct.chipId);

  return chips.reduce<Dictionary<string>>((files, chip) => {
    const chipCts = get(ctsByChip, chip.id, undefined);
    if (!chipCts || chipCts.length === 0) {
      return files;
    }
    files[`${chip.name}.txt`] = buildCtsFileContent(chipCts, qaById ? { qaById } : undefined);
    return files;
  }, {});
}

function buildQaColumnById(ctsRows: CtsColumns[]): Dictionary<string> {
  return ctsRows.reduce<Dictionary<string>>((acc, ct) => {
    acc[ct.id] = formatQaValue(ct);
    return acc;
  }, {});
}

function formatQaValue(ct: CtsColumns): string {
  if (ct.qaRuleId == null) {
    return 'Approved';
  }
  const description = ct.qaRuleDescription?.trim();
  const base = `Rule ${ct.qaRuleId}`;
  return description ? `${base}: ${description}` : base;
}
